/* import __COLOCATED_TEMPLATE__ from './noise.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { userEntryToNumber } from '@ebiz/pretty-num';
import FilterConstants from '../../filter-constants';
import { formatFieldValue } from '../../utils/format';
import ErrorTracker from '../../utils/error-tracker';
import IntlService from 'ember-intl/services/intl';
import type NoiseResult from '../../models/results/sub-results/noise';
import type NoiseAdvisor from '../../models/advisors/noise';
import type NoiseInput from '../../models/inputs/noise';
import { GraphOptions } from '@ebiz/ember-aditools-graph';
import { tracked } from '@glimmer/tracking';

interface NoiseFigureArgs {
  model: NoiseResult;
  options: GraphOptions;
  displayWidth: number;
  displayHeight: number;
  figureInput: NoiseInput;
  advisor: NoiseAdvisor;
  updateInput: (inputType: string, modeldata: NoiseInput) => void;
}

type Error = {
  tab: string | null;
  fields: Array<string>;
  id: string;
  parameters: { [key: string]: string };
};

type Parameter = { fieldId: string; fieldValue: string; status: boolean };

type FieldType = { adcBandwidth: string };

export default class NoiseFigure extends Component<NoiseFigureArgs> {
  // tagName = '';

  ///////////////////////////////////////////////////////////////////////////
  // Template parameters                                                   //
  ///////////////////////////////////////////////////////////////////////////

  // model = null; // Result model from which to draw the figure
  // xTitleKey = null; // Translation key for horizontal axis (for graphs)
  // yTitleKey = null; // Translation key for vertical axis (for graphs)
  // options = null; // Figure display options
  // figureInput = null; // Figure Input
  // workingInput = null; // Working model for the inputs

  // adcBandwidthHelpType = null;
  // adcTableHelpType = null;

  // errorList = null;
  // errorTracker = null;
  @service declare intl: IntlService;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  // init() {
  //   super.init();
  //   set(this, 'workingInput', this.advisor.clone(this.figureInput));
  //   set(this, 'fieldTypes', this.initFieldTypes());
  //   set(
  //     this,
  //     'errorTracker',
  //     ErrorTracker.create({ tabName: FilterConstants.figureName.noise, advisor: this.advisor })
  //   );
  //   set(this, 'errorList', []);
  // }
  @tracked errorList: Array<Error> = [];
  errorTracker = new ErrorTracker(FilterConstants.figureName.noise, this.args.advisor);
  workingInput: NoiseInput = this.args.advisor.clone(this.args.figureInput);
  fieldTypes: FieldType = this.initFieldTypes();

  initFieldTypes(): FieldType {
    return { adcBandwidth: FilterConstants.fieldTypes.Engineering };
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Computed Properties                                                        //
  ////////////////////////////////////////////////////////////////////////////////

  // Changes to the adjustedOptions getter should also be applied to the options getter on the graph figure

  get adjustedOptions(): GraphOptions {
    let yticks = this.args.options.yticks;
    let xticks = this.args.options.xticks;

    if (this.args.options.yscale === 'log' && this.args.options.yticks === -1) {
      const breadth =
        Math.log10(this.args.model.graphData.limits.ymax) - Math.log10(this.args.model.graphData.limits.ymin);
      //console.log(`Decades for ${this.choice.name}: `, breadth);
      yticks = breadth > 2 ? 1 : breadth > 1.5 ? 3 : 9;
    } // We can't afford to overwrite anything in our original
    if (this.args.options.xscale === 'log' && this.args.options.xticks === -1) {
      const breadth =
        Math.log10(this.args.model.graphData.limits.xmax) - Math.log10(this.args.model.graphData.limits.xmin);
      //console.log(`Decades of x for ${this.args.choice.name}: `, breadth);
      xticks = breadth > 3.5 ? 1 : breadth > 1.5 ? 3 : 9;
    }

    const result = Object.assign({}, this.args.options); //clone input
    result.yticks = yticks; //tweak clone
    result.xticks = xticks;

    // Add hover section if available
    result.hover = this.args.options.hover;
    if (result.hover && this.args.options) {
      result.xUnit = this.args.options.xUnit ? this.intl.t(this.args.options.xUnit) : undefined;
      result.yUnit = this.args.options.yUnit ? this.intl.t(this.args.options.yUnit) : undefined;
      if (this.args.options.hoverTitles) {
        const headerLabel = this.args.options.hoverTitles.headerLabel
          ? this.intl.t(this.args.options.hoverTitles.headerLabel)
          : undefined;
        const lineLabels = [];
        for (const lineLabel of this.args.options.hoverTitles.lineLabels) {
          lineLabels.push(this.intl.t(lineLabel));
        }
        if (headerLabel && lineLabels.length > 0) {
          result.hoverTitles = {
            headerLabel: headerLabel,
            lineLabels: lineLabels,
          };
        }
      }
      result.hoverWidthAdjust = this.hoverWidth;
    }

    return result; //ship it
  }

  get hasToleranceData(): boolean {
    const value1 = this.args.model.peakToPeakNoise.worstCaseValue;
    const value2 = this.args.model.rms.worstCaseValue;
    return value1 !== null && value2 !== null;
  }

  get rms1(): string {
    const value = this.args.model.rms.nominalValue;
    return formatFieldValue(value, FilterConstants.fieldTypes.Engineering, '3s');
  }

  get rms2(): string {
    const value = this.args.model.rms.worstCaseValue;
    return formatFieldValue(value, FilterConstants.fieldTypes.Engineering, '3s');
  }

  get peakToPeakNoise1(): string {
    const value = this.args.model.peakToPeakNoise.nominalValue;
    return formatFieldValue(value, FilterConstants.fieldTypes.Engineering, '3s');
  }

  get peakToPeakNoise2(): string {
    const value = this.args.model.peakToPeakNoise.worstCaseValue;
    return formatFieldValue(value, FilterConstants.fieldTypes.Engineering, '3s');
  }

  get isADCBandwidthValid(): boolean {
    return !this.isInErrorList('adcBandwidth');
  }

  get errorMessages(): string[] {
    return this.errorList.map((error) => {
      return this.intl.t(`figures.errors.${error.id}`, error.parameters);
    });
  }

  get adcTableHelp(): { tooltip: string; title: string; message: string } {
    let result = {
      tooltip: 'help.comp-select.noise.adc-table.tooltip',
      title: 'help.comp-select.noise.adc-table.title',
      message: 'help.comp-select.noise.adc-table.message',
    };
    const hasToleranceData = this.hasToleranceData;
    if (hasToleranceData) {
      result = {
        tooltip: 'help.comp-tolerance.noise.adc-table.tooltip',
        title: 'help.comp-tolerance.noise.adc-table.title',
        message: 'help.comp-tolerance.noise.adc-table.message',
      };
    }
    return result;
  }
  get graphHeight(): number {
    return this.args.displayHeight - 130;
  }
  get graphWidth(): number {
    return Math.max(this.args.displayWidth - 275, 350);
  }

  get hoverWidth(): number | undefined {
    switch (this.intl.primaryLocale) {
      case 'en-us':
      case 'ru-ru':
        return 1.05;
      case 'zh-cn':
        return 1.38;
      case 'ja-jp':
        return 1.72;
      default:
        return;
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Utility Functions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  // Function that checks if the field has a parse error, value error or a cross-value error
  // Inputs: fieldName - the field to be checked in the errorList list
  // Returns: true if the fieldName is included in the list, otherwise false
  isInErrorList(fieldName: string): boolean {
    return this.errorList.some((error) => {
      return error.fields.includes(fieldName);
    });
  }

  // Function that does the following:
  validateAndUpdateModel(): void {
    const errors = this.errorTracker.validate(this.workingInput);

    // Update Properties for Handlebar use
    // Have to show error on the figure, not pass on to controller, or you can't see the figure to fix it
    // set(this, 'errorList', errors);
    this.errorList = errors;

    if (errors.length === 0) {
      this.args.updateInput('noise', this.args.advisor.clone(this.workingInput));
    }
  }

  // Function that sets the field input text to the workingModel
  updateModelInput(param: Parameter): void {
    let fieldValue = null;
    try {
      fieldValue = userEntryToNumber(param.fieldValue);
    } catch (e) {
      fieldValue;
    }
    // Get Raw Value after input has been rounded to the correct display value (For Number Field Types Only)
    const rawValue = //fieldValue
      this.fieldTypes[param.fieldId as keyof FieldType] === FilterConstants.fieldTypes.Numeric ||
      this.fieldTypes[param.fieldId as keyof FieldType] === FilterConstants.fieldTypes.NumericDecimal ||
      this.fieldTypes[param.fieldId as keyof FieldType] === FilterConstants.fieldTypes.Engineering
        ? fieldValue
        : param.fieldValue;

    this.workingInput[param.fieldId as keyof NoiseInput] = rawValue;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  @action adcBandwidthChanged(param: Parameter): void {
    this.errorTracker.updateUnparseableList(param);
    this.updateModelInput(param);
    this.validateAndUpdateModel();
  }
}
